import { Dropdown, type MenuProps } from 'antd'
import type { MenuItemType } from 'antd/es/menu/interface'
import classNames from 'classnames'
import { type FC, useState } from 'react'
import { useNavigate, useParams } from 'react-router'

import { PathUtils } from 'routes/routes.utils'

import { SERIES_PAGES_TEXT } from '../pages/shared/seriesPagesText'
import styles from './schedulingMenu.module.scss'

export type SchedulingMenuView = 'calendar' | 'series'

type SchedulingMenuProps = {
  current: SchedulingMenuView
}

// rc-menu spreads extra item fields onto the <li>, which carries the radio semantics.
type SchedulingMenuItem = MenuItemType & {
  key: SchedulingMenuView
  role: 'menuitemradio'
  'aria-checked': boolean
}

const { menu: TEXT } = SERIES_PAGES_TEXT

const OPTIONS: ReadonlyArray<{ key: SchedulingMenuView; label: string; sub: string }> = [
  { key: 'calendar', label: TEXT.calendar, sub: TEXT.calendarSub },
  { key: 'series', label: TEXT.series, sub: TEXT.seriesSub },
]

const isView = (key: string): key is SchedulingMenuView => OPTIONS.some((o) => o.key === key)

export const SchedulingMenu: FC<SchedulingMenuProps> = ({ current }) => {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const { siteId = '' } = useParams()

  const items: SchedulingMenuItem[] = OPTIONS.map((option) => {
    const isCurrent = option.key === current
    return {
      key: option.key,
      role: 'menuitemradio',
      'aria-checked': isCurrent,
      className: classNames(styles.item, { [styles.itemCurrent]: isCurrent }),
      label: (
        <span className={styles.option}>
          <span className={styles.check} aria-hidden='true'>
            {isCurrent ? '✓' : ''}
          </span>
          <span className={styles.texts}>
            <span className={styles.label}>{option.label}</span>
            <span className={styles.sub}>{option.sub}</span>
          </span>
        </span>
      ),
    }
  })

  const onClick: MenuProps['onClick'] = ({ key }) => {
    setOpen(false)
    if (key === current || !isView(key)) return
    navigate(key === 'series' ? PathUtils.getScheduleSeries(siteId) : PathUtils.getSchedule(siteId))
  }

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      trigger={['click']}
      placement='bottomRight'
      menu={{ items, onClick, className: styles.menu }}>
      <button type='button' className={styles.trigger} aria-haspopup='menu' aria-expanded={open}>
        {TEXT.button}
        <span className={styles.chevron} aria-hidden='true'>
          ▾
        </span>
      </button>
    </Dropdown>
  )
}
